import {
  categories as AVAILABLE_CATEGORIES,
  EMPTY_CATEGORY_CONSENT,
} from "constants/categories";
import {
  BANNER_WIDGET_LOCATION_BOTTOM,
  BannerWidgetLocation,
  BoxWidgetLocation,
  CONTINUE_BTN_STYLE_DISABLED,
  IConfigFileScript,
  IWidgetConfigFile,
  PANEL_COOKIES_TAB,
  PANEL_POLICY_TAB,
  PANEL_SETTINGS_TAB,
  WIDGET_TYPE_BANNER,
  WidgetLocations,
} from "types/config-files/mainConfig";
import {
  CONSENT_POLICY_CATEGORY_OPTIN,
  CONSENT_POLICY_GRANULAR_OPTIN,
  CONSENT_TYPE_CATEGORY,
  CONSENT_TYPE_GRANULAR,
  ConsentType,
  CookieCategory,
} from "types/consent";
import { FALSE, NULL, TRUE } from "constants/primitives";

import { DEFAULT_NON_EU_BANNER_COUNTRIES } from "constants/countries";
import { DottedPaths } from "types/generic";
import { SCRIPT_INTEGRATION_METHOD_BANNER_LOAD } from "types/scriptIntegrationMethods";
import { YEAR_IN_SECONDS } from "constants/time";
import getByPath from "helpers/getByPath";
import isArray from "helpers/isArray";
import isString from "helpers/isString";
import isObject from "helpers/isObject";
import includes from "helpers/includes";
import { DEFAULT_NON_EU_BANNER_REGIONS } from "constants/regions";
import { ACCESSIBILITY_BLOCK_LEAVING_DENIED } from "types/accessibility";

type DottedWidgetConfigKey = DottedPaths<IWidgetConfigFile>;

export interface WidgetConfig extends IWidgetConfigFile {
  type: IWidgetConfigFile["widget"]["type"];
  location: IWidgetConfigFile["widget"]["location"];
  buttonsOrder: IWidgetConfigFile["widget"]["buttonsOrder"];
  fontFamily: IWidgetConfigFile["widget"]["fontFamily"];
  consentType: ConsentType;
  isGranularPolicy: boolean;
}

export const createWidgetConfig = (
  config?: Partial<IWidgetConfigFile>
): WidgetConfig => {
  const _config = isObject(config) ? config : {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const get = <T = any, F = null>(
    path: DottedWidgetConfigKey,
    fallback: F | null = NULL
  ) => getByPath<T, F>(_config, path, fallback);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getArray = <T extends any[] = any[], F extends any[] = T>(
    path: DottedWidgetConfigKey,
    fallback: F = [] as unknown as F
  ): T | F => {
    const list = get<T>(path) || fallback;
    return isArray(list) ? (list as T | F) : fallback;
  };

  const getBool = (path: DottedWidgetConfigKey) =>
    !!get<boolean, false>(path, FALSE);

  const consentPolicy = get("consentPolicy") || CONSENT_POLICY_CATEGORY_OPTIN;

  const isGranularPolicy = consentPolicy === CONSENT_POLICY_GRANULAR_OPTIN;

  const scripts = getArray<IConfigFileScript[], IConfigFileScript[]>(
    "scripts"
  ).map((script) => ({
    ...script,
    integration: script.integration || SCRIPT_INTEGRATION_METHOD_BANNER_LOAD,
  }));

  const widgetConfig: WidgetConfig = {
    lastConsentReset: get("lastConsentReset", NULL) || NULL,

    additionalLink: get("additionalLink", "") || "",

    type: get("widget.type", WIDGET_TYPE_BANNER) || WIDGET_TYPE_BANNER,

    buttonsOrder: getArray("widget.buttonsOrder"),

    version: get("version"),

    get location(): IWidgetConfigFile["widget"]["location"] {
      const loc: BoxWidgetLocation | BannerWidgetLocation =
        get("widget.location");
      const locations = WidgetLocations[this.type];
      return includes(locations, loc) ? loc : locations[0];
    },

    get bulkConsent() {
      const bulkConfig = get("bulkConsent") || {};
      return {
        id: getByPath(bulkConfig, "id"),
        group: getByPath(bulkConfig, "group", ""),
        domains: getByPath<string[]>(bulkConfig, "domains", []) || [],
        iframeUrl: getByPath(bulkConfig, "iframeUrl"),
        baseDomain: getByPath(bulkConfig, "baseDomain"),
      };
    },

    consentPolicy: consentPolicy,

    isGranularPolicy: isGranularPolicy,

    privacyPolicyUrl: get("privacyPolicyUrl"),

    showPrivacyUrlInBanner: getBool("showPrivacyUrlInBanner"),

    hideOutsideEU: getBool("hideOutsideEU"),

    showSuffixOnFirstLayer: getBool("showSuffixOnFirstLayer"),

    showNumberOfScriptsInBanner: getBool("showNumberOfScriptsInBanner"),

    loadCssWithLink: getBool("loadCssWithLink"),

    get tabsOnSettingsPanel(): IWidgetConfigFile["tabsOnSettingsPanel"] {
      const key = "tabsOnSettingsPanel";
      const tabsConfig = getArray(key);
      let tabs = [];

      // handle sites without tabs config
      if (!tabsConfig) {
        tabs = [
          PANEL_SETTINGS_TAB,
          PANEL_COOKIES_TAB,
          get("hideCookiePolicy") ? "" : PANEL_POLICY_TAB,
        ];
      } else {
        tabs = tabsConfig;
      }

      if (!includes(tabs, PANEL_SETTINGS_TAB)) {
        tabs.unshift(PANEL_SETTINGS_TAB);
      }
      return tabs.filter((t) => !!t && isString(t));
    },

    languages: getArray("languages"),

    showLanguageSwitcher: getBool("showLanguageSwitcher"),

    banner_continue_button_type:
      get("banner_continue_button_type") || CONTINUE_BTN_STYLE_DISABLED,

    baseConsent: get("baseConsent"),

    /**
     * Pre-consent is the consent object used before any consent is given
     */
    preConsent: { ...EMPTY_CATEGORY_CONSENT, ...(get("preConsent") || {}) },

    scripts: scripts,

    // from all available categories
    cookieCategories: AVAILABLE_CATEGORIES.filter((cat) => {
      const configuredCats = getArray<CookieCategory[]>("cookieCategories");

      // reject those which are not configured for this domain
      if (!includes(configuredCats, cat)) {
        return FALSE;
      }

      // and if we use granular consent, reject those which don't have at least one service assigned
      if (isGranularPolicy) {
        return !!scripts.find((script) => includes(script.categories, cat));
      }

      return TRUE;
    }),

    whitelabel: {
      banner: getBool("whitelabel.banner"),
      panel: getBool("whitelabel.panel"),
      logoUrl: get("whitelabel.logoUrl", NULL),
      linkUrl: get("whitelabel.linkUrl", NULL),
    },

    loadInlineScripts: getBool("loadInlineScripts"),

    okAcceptsAll: getBool("okAcceptsAll"),

    googleConsentModeEnabled: getBool("googleConsentModeEnabled"),
    disableGCMUpdateEvents: getBool("disableGCMUpdateEvents"),
    microsoftConsentModeEnabled: getBool("microsoftConsentModeEnabled"),
    microsoftClarityEnabled: getBool("microsoftClarityEnabled"),
    amazonConsentSignalEnabled: getBool("amazonConsentSignalEnabled"),

    denyBtn: get("denyBtn", "visible"),
    denyBtnSecond: get("denyBtnSecond") || get("denyBtn", "visible"),

    get bannerToggles(): IWidgetConfigFile["bannerToggles"] {
      const showToggles = getBool("bannerToggles");
      return showToggles && this.cookieCategories.length > 1;
    },

    backdropEnabled: getBool("backdropEnabled"),
    backdropColor: get("backdropColor"),
    floatingBtn: {
      // if floatingBtn.isEnabled is not set, use enableFloatingButton as a fallback
      isEnabled: getBool("floatingBtn.isEnabled"),
      icon: get("floatingBtn.icon"),
      location: get("floatingBtn.location"),
    },

    focusBannerFirst: getBool("focusBannerFirst"),

    fontFamily: get("widget.fontFamily", ""),

    widget: get("widget", {
      type: WIDGET_TYPE_BANNER,
      location: BANNER_WIDGET_LOCATION_BOTTOM,
      fontFamily: "",
      buttonsOrder: [],
      width: "",
      boxWidth: "",
    }),

    reconsent: {
      after: get("reconsent.after"),
      check: get("reconsent.check"),
      categories: getArray("reconsent.categories"),
    },

    get branding(): IWidgetConfigFile["branding"] {
      const hasBrandingConfig = !!get("branding");
      return {
        panel: {
          show: hasBrandingConfig
            ? get("branding.panel.show", TRUE)
            : !this.whitelabel.panel,
          url: get("branding.panel.url"),
          logo: get("branding.panel.logo"),
          label: get("branding.panel.label"),
          showLabel: getBool("branding.panel.showLabel"),
        },
        banner: {
          show: hasBrandingConfig
            ? get("branding.banner.show", TRUE)
            : !this.whitelabel.banner,
          url: get("branding.banner.url"),
          logo: get("branding.banner.logo"),
          label: get("branding.banner.label"),
          showLabel: getBool("branding.banner.showLabel"),
        },
      };
    },

    increaseLocationPrecision: getBool("increaseLocationPrecision"),

    /**
     * consentLifetime - number of seconds after which consent is considered expired
     */
    get consentLifetime(): IWidgetConfigFile["consentLifetime"] {
      const lifetime = get("consentLifetime");
      if (!lifetime || typeof lifetime !== "number") {
        return YEAR_IN_SECONDS;
      }

      return lifetime;
    },

    consentType: isGranularPolicy
      ? CONSENT_TYPE_GRANULAR
      : CONSENT_TYPE_CATEGORY,

    bannerNonEuCountries: getArray(
      "bannerNonEuCountries",
      DEFAULT_NON_EU_BANNER_COUNTRIES
    ),

    tcfEnabled: get("tcfEnabled", FALSE),
    tcfLanguages: getArray<string[]>("tcfLanguages"),
    tcfCountry: get("tcfCountry", ""),
    tcfVendors: get("tcfVendors", []),
    tcfGdprApplies: get("tcfGdprApplies", FALSE),
    acEnabled: get("acEnabled", FALSE),
    acVendors: get("acVendors", []),
    gpcAndDntEnabled: getBool("gpcAndDntEnabled"),

    bannerNonEuRegions: getArray(
      "bannerNonEuRegions",
      DEFAULT_NON_EU_BANNER_REGIONS
    ),
    isUsingWildcard: get("isUsingWildcard", FALSE),
    wildcardUrls: get("wildcardUrls", []),
    isInPreviewMode: get("isInPreviewMode", FALSE),
    siteBaseUrl: get("siteBaseUrl", ''),
    accessibilityBlockLeaving: get(
      "accessibilityBlockLeaving",
      ACCESSIBILITY_BLOCK_LEAVING_DENIED
    ),
  };

  return widgetConfig;
};

export default WidgetConfig;
